/**
 * Calendar Subscriptions
 *
 * Hands the user unguessable .ics URLs to paste into Google Calendar, Apple
 * Calendar or Outlook: one personal feed (their own shifts across every
 * eligible org) and one per eligible org (that org's full schedule).
 *
 * Same shape as QrSignupSection — a secret shareable link with copy and
 * revoke — so it borrows that card's idioms deliberately.
 *
 * The refresh-cadence warning below is NOT boilerplate: Google polls external
 * feeds on its own schedule (hours, sometimes a day) and ignores the interval
 * hints we send. Leaving that unsaid turns into "the app is broken" tickets.
 * See docs/CALENDAR_FEED_PLAN.md.
 */

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getCalendarSubscriptions,
  googleAddUrl,
  rotateCalendarToken,
  webcalUrl,
  type CalendarSubscriptions as Subscriptions,
} from '../../api/calendar';
import { getErrorMessage } from '../../api/client';
import ConfirmDialog from '../ui/ConfirmDialog';

interface FeedRowProps {
  title: string;
  subtitle: string;
  url: string;
}

function FeedRow({ title, subtitle, url }: FeedRowProps) {
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="font-medium text-gray-900">{title}</div>
      <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>

      <div className="flex flex-wrap gap-2 mt-3">
        <a
          href={googleAddUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
        >
          Add to Google Calendar
        </a>
        <a
          href={webcalUrl(url)}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Add to Apple Calendar
        </a>
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy link for ${title}`}
          className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Copy link
        </button>
      </div>

      <input
        readOnly
        value={url}
        aria-label={`${title} feed URL`}
        onFocus={(e) => e.currentTarget.select()}
        className="mt-3 w-full text-xs font-mono text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5"
      />
    </div>
  );
}

export default function CalendarSubscriptions() {
  const [subs, setSubs] = useState<Subscriptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    try {
      setSubs(await getCalendarSubscriptions());
      setError(null);
    } catch (err) {
      // Page-load errors render inline; mutations toast.
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const reset = async () => {
    setResetting(true);
    try {
      setSubs(await rotateCalendarToken());
      toast.success('New links created. The old ones no longer work');
      setConfirmingReset(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  // Type guard on url so the eligible rows carry a non-null url — no `!` below.
  const eligible =
    subs?.orgs.filter((o): o is typeof o & { url: string } => o.url !== null) ?? [];
  const ineligible = subs?.orgs.filter((o) => !o.eligible) ?? [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900">Calendar Subscriptions</h2>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Add your ProgramRow schedule to the calendar app you already use.
      </p>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {error && !loading && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {subs && !loading && !error && (
        <div className="space-y-3">
          <FeedRow
            title="My Assignments"
            subtitle="Only the events you're crewed on, across every organization."
            url={subs.personal.url}
          />

          {eligible.map((org) => (
            <FeedRow
              key={org.orgSlug}
              title={`Full Schedule: ${org.orgName}`}
              subtitle="Everything on this organization's schedule, including events you're not on."
              url={org.url}
            />
          ))}

          {ineligible.length > 0 && (
            <p className="text-sm text-gray-500">
              Calendar feeds aren't enabled for{' '}
              {ineligible.map((o) => o.orgName).join(', ')}. Ask an owner there to
              turn them on in Settings → Integrations.
            </p>
          )}

          <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
            <p className="text-sm text-amber-800">
              <span className="font-medium">Heads up:</span> Google Calendar decides
              when to check these links — usually every few hours, sometimes up to a
              day. Great for seeing your schedule at a glance, but{' '}
              <span className="font-medium">not a live view</span>. For time-sensitive
              changes, trust ProgramRow and your event reminders. (Apple Calendar lets
              you set the refresh rate yourself, down to every 5 minutes.)
            </p>
          </div>

          <div className="pt-1">
            <button
              type="button"
              onClick={() => setConfirmingReset(true)}
              className="text-sm text-gray-500 hover:text-red-600 underline"
            >
              Reset my calendar links
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmingReset}
        onClose={() => setConfirmingReset(false)}
        onConfirm={reset}
        isLoading={resetting}
        title="Reset calendar links?"
        // Says what actually happens, rather than "are you sure?". The second
        // sentence is the part users get wrong: revoking stops updates, it
        // does not remove events already synced into their calendar.
        message="Every calendar you've already subscribed will stop receiving updates, and you'll need to re-add the new links. Events already synced stay in your calendar until you delete it there."
        confirmLabel="Reset links"
      />
    </div>
  );
}
